import { forwardRef, useImperativeHandle } from 'react';

import { useIntl } from 'react-intl';

import { Col, Form, Input, Row, Spin } from 'antd';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import { KeyValueWidget } from '@src/components/KeyValueWidget';
import { SortableComponent } from '@src/components/Sortable/SortableComponent';
import { FORM_LAYOUT_DEFAULT } from '@src/constants/commons';
import DivisionsSelector from '@src/containers/DivisionsSelector';
import { ScheduleTemplateSelector } from '@src/containers/ScheduleTemplateSelector';

import { DASHBOARDS_FORMS, TEMPLATE_IDS_DEFAULT } from '../../constants';
import { DEVIATIONS_FROM_TASK_COMPLIANCE_DATA_SOURCE } from './constants';
import { useDeviationsFromTaskCompliance } from './hooks';

const DeviationsFromTaskCompliance = forwardRef(({ chartId, divId, boardId }, formRef) => {
  const intl = useIntl();
  const {
    divisions,
    orgId,
    form,
    handleClear,
    buildBody,
    handleClearTemplateIds,
    onSubmit,
    ColumnItem,
    onSortableMove,
  } = useDeviationsFromTaskCompliance({ chartId, divId, boardId });

  useImperativeHandle(
    formRef,
    () => ({
      ...form,
      handleClear,
      submit: () => form.submit(),
    }),
    [handleClear, form],
  );
  return (
    <Spin spinning={false}>
      <Form
        form={form}
        onValuesChange={(_, allValues) => buildBody(allValues)}
        onFinish={onSubmit}
        name={DASHBOARDS_FORMS.DEVIATIONS_FROM_TASK_COMPLIANCE}
        layout={FORM_LAYOUT_DEFAULT}
        initialValues={{
          title: '',
          description: '',
          divisionIds: [],
          templateIds: TEMPLATE_IDS_DEFAULT,
          dataSourceTable: DEVIATIONS_FROM_TASK_COMPLIANCE_DATA_SOURCE || [],
          formData: {},
        }}
      >
        <BoxContainer content>
          <Row gutter={16}>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    required
                    value={intl.formatMessage({ id: 'dashboards.chart.form.title' })}
                  />
                }
                value={
                  <Form.Item
                    rules={[
                      { required: true, message: intl.formatMessage({ id: 'form.required' }) },
                    ]}
                    name="title"
                  >
                    <Input />
                  </Form.Item>
                }
              />
            </Col>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm
                    value={intl.formatMessage({ id: 'dashboards.chart.form.description' })}
                  />
                }
                value={
                  <Form.Item name="description">
                    <Input.TextArea showCount rows={4} maxLength={250} />
                  </Form.Item>
                }
              />
            </Col>
          </Row>
          {!divId && (
            <Row gutter={16}>
              <Col span={24}>
                <Title.SubTitle value={intl.formatMessage({ id: 'common.division' })} />
              </Col>
              <Col span={24}>
                <KeyValueWidget
                  label={
                    <Title.LabelForm
                      required
                      value={intl.formatMessage({ id: 'common.select.divisions' })}
                    />
                  }
                  value={
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.divisionIds !== currentValues.divisionIds
                      }
                    >
                      {({ getFieldValue }) => {
                        return (
                          <Form.Item name="divisionIds" className="gx-m-0">
                            <DivisionsSelector
                              divisionIds={getFieldValue('divisionIds')}
                              showAllOption={true}
                              allLabelId="divisionSelector.allDivisions"
                              allValue={divisions.map(division => division.value)}
                              onChange={handleClearTemplateIds}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  }
                />
              </Col>
            </Row>
          )}
          <Row gutter={16} className="gx-mt-3">
            <Col span={24}>
              <Title.SubTitle value={intl.formatMessage({ id: 'general.tasks' })} />
            </Col>
            <Col span={24}>
              <KeyValueWidget
                label={
                  <Title.LabelForm required value="Seleccione las tareas que deseas visualizar" />
                }
                value={
                  <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                      prevValues.divisionIds !== currentValues.divisionIds
                    }
                  >
                    {({ getFieldValue }) => {
                      return (
                        <Form.Item name="templateIds">
                          <ScheduleTemplateSelector
                            organizationId={orgId}
                            divisionIds={getFieldValue('divisionIds') || []}
                            templateIds={getFieldValue('templateIds')}
                          />
                        </Form.Item>
                      );
                    }}
                  </Form.Item>
                }
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24} className="gx-mb-3">
              <Title.SubTitle value={intl.formatMessage({ id: 'chart' })} />
            </Col>
            <Col span={24}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  JSON.stringify(prevValues.dataSourceTable) !==
                  JSON.stringify(currentValues.dataSourceTable)
                }
              >
                {({ getFieldValue }) => {
                  return (
                    <Form.Item name="dataSourceTable">
                      <SortableComponent
                        items={getFieldValue('dataSourceTable')}
                        Component={ColumnItem}
                        onMove={onSortableMove}
                        zIndex={1000}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </BoxContainer>
        <Form.Item name="formData" hidden />
        <Form.Item name="id" hidden />
      </Form>
    </Spin>
  );
});

export default DeviationsFromTaskCompliance;
